/**
 * Minimal CSV parser for the bulk-import feature. No external dependency.
 *
 * - First non-empty line is the header row; header keys are trimmed and
 *   lowercased for tolerant matching.
 * - Handles quoted fields ("...") that contain commas, newlines, and escaped
 *   double-quotes ("").
 * - Skips fully blank lines.
 */
export interface ParsedCsv {
  headers: string[];
  rows: Record<string, string>[];
}

function splitRecords(text: string): string[][] {
  const records: string[][] = [];
  let field = "";
  let record: string[] = [];
  let inQuotes = false;

  const pushField = () => { record.push(field); field = ""; };
  const pushRecord = () => { records.push(record); record = []; };

  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') { field += '"'; i++; } // escaped quote
        else inQuotes = false;
      } else {
        field += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ",") {
      pushField();
    } else if (ch === "\n") {
      pushField();
      pushRecord();
    } else if (ch === "\r") {
      // ignore — handles \r\n and lone \r
    } else {
      field += ch;
    }
  }
  // flush trailing field/record
  if (field.length > 0 || record.length > 0) {
    pushField();
    pushRecord();
  }
  return records;
}

export function parseCsv(text: string): ParsedCsv {
  const records = splitRecords(text)
    .filter((r) => r.some((c) => c.trim() !== "")); // drop blank lines

  if (records.length === 0) return { headers: [], rows: [] };

  const headers = records[0].map((h) => h.trim().toLowerCase());
  const rows: Record<string, string>[] = [];

  for (let i = 1; i < records.length; i++) {
    const cells = records[i];
    const row: Record<string, string> = {};
    headers.forEach((h, idx) => {
      row[h] = (cells[idx] ?? "").trim();
    });
    rows.push(row);
  }

  return { headers, rows };
}
